import {
    CellClassParams,
    CellStyle,
    ColDef,
    Column,
    ColumnResizedEvent,
    ColumnState,
    ICellRendererParams,
    IRowNode,
    NewValueParams,
    ValueFormatterParams,
    ValueGetterParams,
    ValueParserParams,
} from '@ag-grid-community/core';
import { ElementRef, Injectable } from '@angular/core';
import { CurrentStateService } from '@ddv/behaviors';
import { ColorMetadataService } from '@ddv/charts';
import { CrosstalkModalService } from '@ddv/crosstalk';
import {
    ColumnMinMax,
    CustomColDef,
    CustomColGroupDef,
    DataGridComponent,
    DataGridOptions,
    DefaultDataGridOptions,
    GridConfiguration,
    GridState,
} from '@ddv/data-grid';
import { ErrorLoggerService } from '@ddv/error-handling';
import { booleanFormatter, dateFormatter, FormatterService, numberFormatter, percentageFormatter } from '@ddv/formatters';
import { ManagerService } from '@ddv/layout';
import {
    AGFilter,
    AgGridFilterState,
    AgGridSortDirection,
    AppWidgetState,
    AUTO_GROUP_COLUMN_ID,
    crosstalkCheckboxFieldId,
    CrosstalkFields,
    CustomFilterParams,
    EDITABLE_CHECKBOX_FIELD,
    ExportColumn,
    getCurrentVisualizationId,
    GRID_ICONS,
    CellFormat,
    isTradeFileDetails,
    LinkConfiguration,
    MANAGE_WIDGET_ID,
    MetadataFormatUtils,
    MetadataLookup,
    NUMBER_FILTER_OPERATIONS,
    NUMBER_FILTER_OPERATIONS_FOR_RANKED_COLUMN,
    NumberFormat,
    ColumnDisplayType,
    ColumnNegativeValues,
    TFL_DETAILS_AUTO_GROUP_COLUMN_ID,
    TRANSACTION_TYPE_FIELD,
    TrebekConversationFields,
    UserDefinedFieldType,
    VisualizationConfigs,
    NamedQuery,
    WidgetData,
    GridConfigItem,
    FieldMetadata,
    moTradeReleaseCommentFieldId,
} from '@ddv/models';
import { camelCaseString, clone, deepClone, deepExtend, getMaximumValue, getMinimumValue } from '@ddv/utils';
import { CommonDate } from '@hs/ui-core-date';

import { ColumnRankingService, RANK_SUFFIX } from './column-ranking.service';
import { ColumnTransformService } from './column-transform.service';
import { getCellStyle } from './get-cell-style';
import {
    isAgGridDateFilterState,
    isAgGridDateMultiConditionFilterState,
    isAgGridNumberFilterState,
    isAgGridNumberMultiConditionFilterState,
    isAgGridSetFilterState,
    isGridDateFilter,
    isGridDateMultiConditionFilter,
    isGridNumberFilter,
    isGridNumberMultiConditionFilter,
    isGridSetFilter,
} from './grid-types';
import { GridUriBuilderService } from './grid-uri-builder.service';
import { UserGridColumnOverridesService } from './user-grid-column-overrides.service';
import { SelectedWidgetRelayService } from '../../base/selected-widget-relay.service';
import { CheckboxCellRendererComponent } from '../cell-renderers/checkbox-cell-renderer/checkbox-cell-renderer.component';
import {
    ColumnConditionCellRendererComponent,
} from '../cell-renderers/column-condition-cell-renderer/column-condition-cell-renderer.component';
import { URLCellRendererComponent } from '../cell-renderers/url-cell-renderer/url-cell-renderer.component';
import { CheckboxHeaderComponent } from '../headers/checkbox-header/checkbox-header.component';

@Injectable()
export abstract class GridConfigService {
    protected customCheckboxColumnDefinition: ColDef = {
        field: EDITABLE_CHECKBOX_FIELD,
        headerComponent: CheckboxHeaderComponent,
        headerName: '',
        lockPosition: true,
        pinned: 'left',
        lockPinned: true,
        resizable: false,
        width: 34,
        minWidth: 34,
        maxWidth: 34,
        pivot: false,
        enablePivot: false,
        hide: false,
        lockVisible: true,
        suppressColumnsToolPanel: true,
        filter: false,
        cellRenderer: CheckboxCellRendererComponent,
    };
    protected isInViewMode = false;
    protected visualizationType = 'SIMPLE_GRID';

    private readonly rowHeights = MetadataFormatUtils.getRowHeights();
    private datasetMinMax: { [namedQueryId: string]: { [columnId: string]: ColumnMinMax } } = {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private editedCellsOriginalValue: Record<number, any> = {};
    private isMultiClient = false;

    constructor(
        protected formatter: FormatterService,
        protected errorLoggerService: ErrorLoggerService,
        protected columnRankingService: ColumnRankingService,
        protected columnTransformService: ColumnTransformService,
        protected manager: ManagerService,
        protected crosstalkModalService: CrosstalkModalService,
        protected currentStateService: CurrentStateService,
        protected userGridColumnOverridesService: UserGridColumnOverridesService,
        protected selectedWidgetRelayService: SelectedWidgetRelayService,
        private readonly gridBuilderService: GridUriBuilderService,
        private readonly colorMetadataService: ColorMetadataService,
    ) {
        this.currentStateService.isMultiClient$.subscribe((isMultiClient: boolean) => this.isMultiClient = isMultiClient);
        this.currentStateService.dashboardModeAndId$.subscribe(({ mode }) => this.isInViewMode = mode === 'view');
    }

    getGridConfiguration(
        vizConfigs: VisualizationConfigs,
        widgetId: number,
        namedQueryId: string,
        metadata: MetadataLookup = {},
    ): GridConfiguration {
        return {
            columnDefinitions: this.getGridColumns(vizConfigs, widgetId, namedQueryId, metadata),
            uniqueId: `${widgetId}-grid`,
            autoFitColumns: false,
            autoSizeColumns: vizConfigs.autoSizeColumns,
            gridOptions: this.getGridOptions(vizConfigs, widgetId),
            showQuickSearch: vizConfigs.showQuickSearch,
            distinctRowsOnly: vizConfigs.distinctRowsOnly,
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getGridOptions(gridFormat: VisualizationConfigs, widgetId?: number): DataGridOptions {
        const gridOptions: DataGridOptions = new DefaultDataGridOptions();
        gridOptions.icons = {};
        for (const icon in GRID_ICONS) {
            if (icon) {
                gridOptions.icons[icon] = `<i class="${GRID_ICONS[icon]}"/>`;
            }
        }
        gridOptions.icons.filter = `<i class="${GRID_ICONS.filter} ag-icon-filter"/>`;
        gridOptions.rowClass = gridFormat.alternateRowShading ? 'grid-alternateshade' : '';
        gridOptions.autoSizePadding = 8;
        gridOptions.headerHeight = gridFormat.hideGridHeaders ? 0 : 40;
        gridOptions.rowHeight = gridFormat.rowHeight ?? 25;
        gridOptions.defaultColDef = {};
        return gridOptions;
    }

    getRankedColumnIds(columnConfigs: GridConfigItem[]): string[] {
        return this.columnRankingService.getRankedColumnIds(columnConfigs);
    }

    getGridColumns(vizConfigs: VisualizationConfigs, widgetId: number, namedQueryId: string, metadata: MetadataLookup): ColDef[] {
        const columns: ColDef[] = (vizConfigs.configs?.values as GridConfigItem[]).map((columnConfig) => {
            const metadataForColumn = metadata[columnConfig.value ?? ''];
            const columnDef = this.getGridColumn(
                vizConfigs,
                columnConfig,
                widgetId,
                namedQueryId,
                deepClone(metadataForColumn));
            if (columnConfig.displayType === 'value' && (columnConfig.datatype === 'percentage' || columnConfig.datatype === 'decimal')) {
                this.addCustomNumberSort(columnDef);
            }
            if (columnConfig.displayType === 'string' && columnConfig.datatype === 'string') {
                this.addCustomStringSort(columnDef);
            }
            if (columnConfig.isRanked) {
                this.columnRankingService.addRankHandlersToColumnDef(columnDef, columnConfig);
            }
            if (columnConfig.columnCondition?.length) {
                this.addColumnConditionCellRenderer(columnDef, columnConfig, vizConfigs, namedQueryId, metadataForColumn);
            }
            if (this.columnShouldBeLinkified(columnConfig, columnDef, metadataForColumn)) {
                this.addURLCellRenderer(columnDef, columnConfig, vizConfigs, namedQueryId, metadataForColumn);
            }
            if (this.isUserDefinedFieldColumn((columnDef as CustomColDef).field)) {
                (columnDef as CustomColDef).userDefinedFieldType = columnConfig.datatype as UserDefinedFieldType;
                if (columnConfig.datatype === 'decimal') {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (columnDef as CustomColDef).valueFormatter = (params: ValueFormatterParams): any => {
                        return params.node ?
                            this.getFormattedValue(params, columnConfig, vizConfigs, namedQueryId, metadataForColumn) :
                            null;
                    };
                }
            }
            return columnDef;
        }) ?? [];

        // The below definitely doesn't belong here.
        // We should move it in the Advanced Grid Config Service.
        const widgetPrefs = this.getWidgetPreferences(widgetId);
        const isAdvancedGrid = widgetPrefs?.currentVisualization === 'ADVANCED_GRID';
        const conversableType = !!widgetPrefs?.namedQuery?.normalizeConversableType();
        const isCrosstalkGrid = isAdvancedGrid && conversableType;
        if (isCrosstalkGrid && !isManageWidgetMode(widgetId)) {
            const visualizationId = getCurrentVisualizationId(widgetPrefs)!;
            this.userGridColumnOverridesService.setCheckboxColumnPosition(
                widgetId,
                visualizationId,
                columns,
                'ColDef',
                this.isInViewMode,
                widgetPrefs?.currentVisualization,
            );
        }

        return columns;
    }

    // eslint-disable-next-line complexity
    getGridColumn(
        gridFormat: VisualizationConfigs,
        columnConfig: GridConfigItem,
        widgetId: number,
        namedQueryId: string,
        metadata: FieldMetadata | undefined,
    ): CustomColDef | CustomColGroupDef {
        // Disable ability to group UDF columns
        // and disband already saved UDF groups
        // until we start fetching the data for them from Trebek
        if (this.isUserDefinedFieldColumn(columnConfig.value)) {
            if (metadata) {
                // Changing a deep clone of metadata to avoid mutating the original
                metadata.canPivotOn = false;
            }
            columnConfig.rowGroupIndex = null;
        }

        return {
            headerName: columnConfig.showCustomName ? columnConfig.customName ?? undefined : columnConfig.label,
            field: this.columnRankingService.getFieldName(columnConfig),
            colId: columnConfig.colId,
            groupId: metadata?.agBand ?? undefined,
            enableRowGroup: !!metadata?.canPivotOn,
            pivot: !!metadata?.canPivotOn && columnConfig.pivotIndex != null,
            enablePivot: !!metadata?.canPivotOn,
            pivotIndex: columnConfig.pivotIndex,
            enableValue: columnConfig.displayType === 'value',
            hide: columnConfig.isHidden ?? undefined,
            suppressColumnsToolPanel: false,
            suppressSpanHeaderHeight: true,
            pinned: columnConfig.pinned,
            rowGroup: !!columnConfig.rowGroupIndex || columnConfig.rowGroupIndex === 0,
            rowGroupIndex: columnConfig.rowGroupIndex,
            width: columnConfig.width ?? undefined,
            minWidth: 50,
            customWidthWhenGroup: columnConfig.rowGroupIndex != null ?
                (columnConfig.customWidthWhenGroup ?? undefined) :
                undefined,
            headerTooltip: gridFormat.showColumnHeaderTooltip ? metadata?.description : undefined,
            cellClass: (params): string[] => this.getColumnConfigClass(params, columnConfig, gridFormat, widgetId),
            cellStyle: (params): CellStyle => this.renderCellStyle(params, columnConfig),
            cellRenderer: (params: ICellRendererParams): string | undefined => {
                return !columnConfig.columnCondition?.length ?
                    this.getFormattedValue(params, columnConfig, gridFormat, namedQueryId, metadata, widgetId) :
                    undefined;
            },
            aggFunc: columnConfig.aggregationType === 'none' ? null : columnConfig.aggregationType,
            filter: this.getColumnFilterType(columnConfig.displayType),
            filterParams: this.getColumnFilterParams(columnConfig, widgetId),
            filterValueGetter: (params: ValueGetterParams) => this.getColumnFilterValueGetter(params, columnConfig),
            unSortIcon: true,
            getQuickFilterText: (params): string => !params.column.isVisible() ? '' : params.value,
            editable: this.visualizationType === 'ADVANCED_GRID' ? !!metadata?.editable : false,
            valueParser: (params: ValueParserParams): string | number => getParsedValue(params),
            onCellValueChanged: (params): void => this.onCellValueChanged(params, widgetId),
        };
    }

    getUpdatedColumnConfigs(preferences: VisualizationConfigs, metadata: MetadataLookup = {}): GridConfigItem[] {
        const udfColumnNames = Object.keys(metadata).filter((metadataKey) => this.isUserDefinedFieldColumn(metadataKey));
        preferences.rowHeight = preferences.rowHeight ?? 20;

        const columnConfigs: GridConfigItem[] = [];
        if (preferences.configs?.values) {
            preferences.configs.values.forEach((element) => {
                if (!this.isUserDefinedFieldColumn(element.value) || udfColumnNames.includes(element.value)) {
                    const columnConfig = this.getUpdatedColumnConfig(element as GridConfigItem);
                    columnConfigs.push(columnConfig);
                }
            });
        }
        return columnConfigs;
    }

    getUpdatedColumnConfig(element: GridConfigItem): GridConfigItem {
        const columnConfig = clone(element);
        columnConfig.customName = (element.showCustomName ? element.customName : element.label) ?? null;
        columnConfig.columnCondition = [];
        element.columnCondition?.forEach((columnCondition) => {
            const columnFormat: CellFormat = clone(columnCondition);
            if (columnCondition.operatorCondition === 'EQL' || columnCondition.operatorCondition === 'NEQL') {
                columnFormat.rangeFrom = columnFormat.value;
            } else {
                columnFormat.rangeFrom = columnFormat.value ?? columnFormat.rangeFrom;
            }
            columnConfig.columnCondition?.push(columnFormat);
        });
        return columnConfig;
    }

    columnResizeFormatter(ref: ElementRef, event: ColumnResizedEvent): void {
        if (!Object.prototype.hasOwnProperty.call(event, 'finished') || !event.column) {
            return;
        }

        const columns = ref.nativeElement.querySelectorAll(`div[col-id = '${event.column.getColId().replace('!', '\\!')}']`);
        for (const column of columns) {
            if (event.finished) {
                column.classList.remove('resize-border');
            } else {
                column.classList.add('resize-border');
            }
        }
    }

    getExportFilteredData(
        dataGridComponent: DataGridComponent | undefined,
        preferences: VisualizationConfigs | undefined,
        isCSV = false,
        onlySelectedRows = false,
    ): WidgetData[] {
        if (!dataGridComponent) {
            return [];
        }

        const visibleColumns = dataGridComponent.getAllDisplayedColumns() ?? [];
        const allColumns = dataGridComponent.getAllColumns();
        let exportColumns = this.getExportColumns(visibleColumns).filter((column) => {
            return column.columnId !== EDITABLE_CHECKBOX_FIELD && column.columnId !== crosstalkCheckboxFieldId;
        });
        const autoColumnGroupIndex = exportColumns.findIndex((column) => column.columnId === AUTO_GROUP_COLUMN_ID);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let rowData: any[] = [];
        this.addGrandTotalRow(dataGridComponent, preferences, isCSV, allColumns, rowData);
        const { columnState, pivotMode: inPivotMode } = dataGridComponent.getState();
        const autoColumnGroup = columnState.find((col) => col.colId === AUTO_GROUP_COLUMN_ID);

        // We start from 1 instead of 0 because we either:
        // - set the grand total row _row_id = 0
        // - or set the empty 'Control Total' row _row_id = 0 in DDV MW
        let index = 1;
        dataGridComponent.grid.api?.forEachNodeAfterFilterAndSort((rowDatum: IRowNode) => {
            if (!rowDatum || !this.shouldBeExported(onlySelectedRows, rowDatum)) {
                return;
            }

            const data = this.getExportRowData(
                rowDatum,
                inPivotMode,
                !!autoColumnGroup,
                index,
                isCSV,
                preferences,
                allColumns,
                dataGridComponent,
            );

            if (data) {
                if (preferences?.isAdvancedGrid?.()) {
                    rowData.push({
                        ...data,
                        _row_id: index,
                        id: rowDatum.id,
                        group: rowDatum.group,
                    });
                    index += 1;
                } else {
                    rowData.push(data);
                }
            }
        });

        if (preferences?.isAdvancedGrid?.() && !isCSV) {
            rowData.forEach((rowDatum) => {
                const parent = rowData.find((row) => row.childrenIds?.includes(rowDatum.id));
                if (rowDatum._row_id !== 0) {
                    rowDatum._parent_id = parent ? parent._row_id : 0;
                }
            });
        }

        const rowGroupColumns = dataGridComponent.getRowGroupColumns() ?? [];
        if (autoColumnGroupIndex !== -1) {
            if (isCSV) {
                exportColumns = this.getExportColumnsForGroupedGrid(exportColumns, rowGroupColumns, onlySelectedRows);
                rowData = rowData.filter((rowDatum) => {
                    return !Object.keys(rowDatum).includes(AUTO_GROUP_COLUMN_ID);
                });
            } else {
                exportColumns.splice(autoColumnGroupIndex, 1);
                const groupColumns = this.getExportColumns(rowGroupColumns);
                exportColumns.unshift(...groupColumns);
                return this.getExportData(exportColumns, rowData, allColumns, groupColumns, dataGridComponent?.datasetIncludesCrosstalk);
            }
        }

        return this.getExportData(exportColumns, rowData, allColumns, undefined, dataGridComponent?.datasetIncludesCrosstalk);
    }

    getExportFilteredDataGroupers(dataGridComponent?: DataGridComponent): string[] {
        if (!dataGridComponent) {
            return [];
        }

        const rowGroupColumns = dataGridComponent.getRowGroupColumns() ?? [];
        return this.getExportColumns(rowGroupColumns).map((column) => column.columnName);
    }

    getFilteredExportVisibleColumns(dataGridComponent?: DataGridComponent): string[] {
        const visibleColumns = dataGridComponent?.getAllDisplayedColumns() ?? [];
        return this.getExportColumns(visibleColumns)
            .filter((vc) =>
                vc.columnId !== AUTO_GROUP_COLUMN_ID &&
                vc.columnId !== EDITABLE_CHECKBOX_FIELD &&
                vc.columnId !== crosstalkCheckboxFieldId &&
                vc.columnId !== CrosstalkFields.CommentCounter)
            .map((c) => c.columnName);
    }

    getExportFullData(dataGridComponent?: DataGridComponent): WidgetData[] {
        if (!dataGridComponent) {
            return [];
        }

        const columns = dataGridComponent.getAllColumns().filter((col) => col.getColDef()?.field !== CrosstalkFields.CommentCounter);
        const exportColumns = this.getExportColumns(columns);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const rowData: any[] = [];

        dataGridComponent.grid.api?.forEachNode((node) => {
            if (!node.group) {
                rowData.push(node.data);
            }
        });

        return rowData.map((currRowData) => {
            const row: WidgetData = {};
            exportColumns.forEach((value) => {
                row[value.columnId] = typeof currRowData[value.columnId] !== 'undefined' ? currRowData[value.columnId] : '';
            });
            return row;
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateDataset(columnConfigs: GridConfigItem[], namedQueryId: string, gridData: any[]): void {
        this.columnTransformService.transformAbsoluteValue(gridData, columnConfigs);
        this.columnRankingService.rankData(gridData, columnConfigs);
        this.updateDatasetMinMax(columnConfigs, namedQueryId, gridData);
        this.removeNullStrings(columnConfigs, gridData);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    removeNullStrings(columnConfigs: GridConfigItem[], gridData: any[]): void {
        columnConfigs.forEach((config) => {
            gridData.forEach((dataRow) => {
                if (config.datatype === 'string' && config.value) {
                    dataRow[config.value] = dataRow[config.value] ?? '';
                }
            });
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateDatasetMinMax(columnConfigs: GridConfigItem[], namedQueryId: string, gridData: any[]): void {
        if (!this.datasetMinMax[namedQueryId]) {
            this.datasetMinMax[namedQueryId] = {};
        }
        columnConfigs.forEach((columnConfig) => {
            if (columnConfig.displayType === 'bar') {
                this.datasetMinMax[namedQueryId][columnConfig.value] = {
                    min: getMinimumValue(gridData, columnConfig.value) ?? 0,
                    max: getMaximumValue(gridData, columnConfig.value) ?? 0,
                };
            }
        });
    }

    initializeGridFiltersAndSorts(columnConfigs: GridConfigItem[] | undefined, gridState: GridState | undefined): void {
        if (!gridState) {
            return console.error('Cannot initializeGridFiltersAndSorts without a gridState');
        }

        gridState.sortState = [];

        columnConfigs?.forEach((columnConfig) => {
            if (isGridSetFilter(columnConfig.agFilter)) {
                try {
                    gridState.filterState[columnConfig.colId] = {
                        filterType: 'set',
                        values: JSON.parse(columnConfig.agFilter.includedStrings),
                    };
                } catch (ex) {
                    console.error(`Invalid string filter: ${columnConfig.agFilter.includedStrings} for column ${columnConfig.colId}`);
                }
            } else if (isGridDateMultiConditionFilter(columnConfig.agFilter)) {
                gridState.filterState[columnConfig.colId] = {
                    conditions: [JSON.parse(columnConfig.agFilter.condition1), JSON.parse(columnConfig.agFilter.condition2)],
                    filterType: columnConfig.agFilter.filterType,
                    operator: columnConfig.agFilter.operator,
                };
            } else if (isGridDateFilter(columnConfig.agFilter)) {
                gridState.filterState[columnConfig.colId] = {
                    dateFrom: columnConfig.agFilter.filterFrom,
                    dateTo: columnConfig.agFilter.filterTo ?? '',
                    filterType: columnConfig.agFilter.filterType,
                    type: columnConfig.agFilter.type,
                };
            } else if (isGridNumberMultiConditionFilter(columnConfig.agFilter)) {
                gridState.filterState[columnConfig.colId] = {
                    conditions: [JSON.parse(columnConfig.agFilter.condition1), JSON.parse(columnConfig.agFilter.condition2)],
                    filterType: columnConfig.agFilter.filterType,
                    operator: columnConfig.agFilter.operator,
                };
            } else if (isGridNumberFilter(columnConfig.agFilter)) {
                gridState.filterState[columnConfig.colId] = {
                    filter: columnConfig.agFilter.filterFrom != null ? Number(columnConfig.agFilter.filterFrom) : 0,
                    filterTo: columnConfig.agFilter.filterTo != null ? Number(columnConfig.agFilter.filterTo) : 0,
                    filterType: columnConfig.agFilter.filterType,
                    type: columnConfig.agFilter.type,
                };
            }

            if (columnConfig.agSort) {
                gridState.sortState.push({
                    colId: columnConfig.colId,
                    sort: columnConfig.agSort.sort,
                    sortIndex: columnConfig.agSortIndex ?? gridState.sortState.length,
                });
            }
        });
    }

    initializeGroupSort(sort: AgGridSortDirection | undefined | null, sortIndex: number, gridState: GridState): void {
        for (let i = 0; i < gridState.sortState.length; i++) {
            if (!gridState.sortState[i]) {
                gridState.sortState[i] = {
                    colId: AUTO_GROUP_COLUMN_ID,
                    sort,
                    sortIndex,
                };
                return;
            }
        }
        gridState.sortState.push({
            colId: AUTO_GROUP_COLUMN_ID,
            sort,
            sortIndex,
        });
    }

    updateFiltersInManager(widgetId: number, vizConfigs?: VisualizationConfigs): void {
        const managerPrefs = this.manager.getWidgetById(widgetId)?.getExtraPreferences();
        if (managerPrefs?.visualizationConfigs) {
            const agIndex = managerPrefs.visualizationConfigs.findIndex((config) => config.visualizationType === this.visualizationType);
            (managerPrefs.visualizationConfigs[agIndex].configs?.values as GridConfigItem[]).forEach((colConfig, i) => {
                colConfig.agFilter = (vizConfigs?.configs?.values as GridConfigItem[])[i]?.agFilter;
            });
            this.manager.setWidgetExtraPreferences(widgetId, managerPrefs);
        }
    }

    updateSortsInManager(widgetId: number, vizConfigs?: VisualizationConfigs): void {
        const managerPrefs = this.manager.getWidgetById(widgetId)?.getExtraPreferences();
        if (managerPrefs?.visualizationConfigs) {
            const agIndex = managerPrefs.visualizationConfigs.findIndex((config) => config.visualizationType === this.visualizationType);
            (managerPrefs.visualizationConfigs[agIndex].configs?.values as GridConfigItem[]).forEach((colConfig, i) => {
                colConfig.agSort = (vizConfigs?.configs?.values as GridConfigItem[])[i]?.agSort;
                colConfig.agSortIndex = (vizConfigs?.configs?.values as GridConfigItem[])[i]?.agSortIndex;
            });
            managerPrefs.visualizationConfigs[agIndex].groupSort = vizConfigs?.groupSort;
            managerPrefs.visualizationConfigs[agIndex].groupSortIndex = vizConfigs?.groupSortIndex;
            this.manager.setWidgetExtraPreferences(widgetId, managerPrefs);
        }
    }

    updateColumnFilterFromState(widgetId: number, gridState: GridState | undefined, vizConfigs?: VisualizationConfigs): void {
        (vizConfigs?.configs?.values as GridConfigItem[]).forEach((columnConfig) => {
            columnConfig.agFilter = this.getFilterFromState(gridState?.filterState[columnConfig.colId]);
        });
        this.updateFiltersInManager(widgetId, vizConfigs);
    }

    updateColumnSortFromState(widgetId: number, gridState: GridState | undefined, vizConfigs: VisualizationConfigs | undefined): void {
        (vizConfigs?.configs?.values as GridConfigItem[]).forEach((columnConfig) => {
            const column = gridState?.sortState.find((sort) => columnConfig.colId === sort.colId);
            columnConfig.agSort = column?.sort ? { sort: column.sort } : null;
            columnConfig.agSortIndex = (column?.sortIndex !== undefined ? column.sortIndex : null) ?? null;
        });

        const groupSort = gridState?.sortState.find((ss) => ss.colId === AUTO_GROUP_COLUMN_ID);
        if (isManageWidgetMode(widgetId) && groupSort && vizConfigs) {
            vizConfigs.groupSort = groupSort.sort;
            vizConfigs.groupSortIndex = groupSort.sortIndex ?? undefined;
        }

        this.updateSortsInManager(widgetId, vizConfigs);
    }

    getCurrentWidgetNamedQuery(widgetId: number): NamedQuery | undefined {
        return this.manager.getWidgetById(widgetId)?.extraParameters?.preferences?.namedQuery;
    }

    clearEditedCellsOriginalValue(): void {
        this.editedCellsOriginalValue = {};
    }

    orderColumnsInColumnState(columnState: ColumnState[], columns: GridConfigItem[]): ColumnState[] {
        const editableCheckboxColumn = columnState.find((state) => state.colId === EDITABLE_CHECKBOX_FIELD);
        const autoColumnGroup = columnState.find((state) => state.colId === AUTO_GROUP_COLUMN_ID);
        const orderMap = new Map<string, number>(columns.map((col, index) => [col.colId, index]));

        let newColumnState = columnState.filter((column) => columns.find((state) => state.colId === column.colId));
        newColumnState.sort((a, b) => {
            const aOrder = orderMap.get(a.colId) ?? Number.POSITIVE_INFINITY;
            const bOrder = orderMap.get(b.colId) ?? Number.POSITIVE_INFINITY;
            return aOrder - bOrder;
        });

        if (autoColumnGroup) {
            const checkboxColumnIndex = newColumnState.findIndex((col) => col.colId === crosstalkCheckboxFieldId);
            if (checkboxColumnIndex === 0) {
                newColumnState.splice(1, 0, autoColumnGroup);
            } else {
                newColumnState = [autoColumnGroup, ...newColumnState];
            }
        }

        if (editableCheckboxColumn) {
            newColumnState = [editableCheckboxColumn, ...newColumnState];
        }

        return newColumnState;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
    columnIsExternallyLinked(columnId: string, rowData: any): boolean {
        return !!this.getExternalLinkConfiguration(columnId, rowData);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
    getExternalLinkConfiguration(columnId: string, rowData: any): LinkConfiguration {
        const config: LinkConfiguration = rowData?.links?.[columnId];
        if (config) {
            config.columnId = columnId;
        }

        return config;
    }

    protected getWidgetPreferences(widgetId: number): AppWidgetState | undefined | null {
        return this.manager.getWidgetPreferences(widgetId);
    }

    protected getCurrentNamedQuery(widgetId: number): NamedQuery | undefined {
        return isManageWidgetMode(widgetId) ?
            this.selectedWidgetRelayService.getCurrentNamedQuery() :
            this.getCurrentWidgetNamedQuery(widgetId);
    }

    protected isEditableUserDefinedFieldColumn(field: string | undefined): boolean {
        return !!(this.isUserDefinedFieldColumn(field) && !(field?.endsWith('_commentTS') || field?.endsWith('_commenter')));
    }

    protected isUserDefinedFieldColumn(field: string | undefined): boolean {
        return !!field?.startsWith('udf_');
    }

    private getColumnConfigClass(
        params: CellClassParams,
        columnConfig: GridConfigItem,
        gridFormat: VisualizationConfigs,
        widgetId: number,
    ): string[] {
        const columnConfigClass: string[] = [columnConfig.fontStyle ?? '', columnConfig.alignment ?? ''];
        const camelCasedLabel = camelCaseString(this.rowHeights.find((rowHeight) => rowHeight.value === gridFormat.rowHeight)?.label ?? '');
        columnConfigClass.push(camelCasedLabel);
        if (!gridFormat.alternateRowShading) {
            columnConfigClass.push('no-grid-alternateshade');
        }
        if (this.editedCellsOriginalValue?.[widgetId]?.[params.node.id!]?.[params.colDef.colId!]?.hasChanged) {
            columnConfigClass.push('ag-cell-edited');
        }

        if (columnConfig.columnCondition?.length) {
            columnConfigClass.push('conditional-format');
        }
        return columnConfigClass;
    }

    private renderCellStyle(params: CellClassParams, columnConfig: GridConfigItem): CellStyle {
        if (columnConfig.colId === crosstalkCheckboxFieldId) {
            return {};
        }

        let cellValue = params.value;
        if (this.isUserDefinedFieldColumn(columnConfig.value)) {
            if (typeof cellValue === 'object' && !cellValue?.value) {
                return { color: '', backgroundColor: '' };
            }

            if (typeof cellValue === 'object') {
                cellValue = params.node.aggData ?
                    cellValue.value :
                    this.assignUDFValue(cellValue?.type, cellValue?.value);
            }

            if (params.value?.type === 'boolean') {
                // currently we are not doing anything for the boolean we can't/should not change their colors
                return {};
            }
        }

        if (columnConfig.negativeValue === 'redMinus' || columnConfig.negativeValue === 'redParenthesis') {
            if (typeof cellValue === 'number' && cellValue < 0) {
                return { color: 'var(--error-color)' };
            }
        }

        return this.getCellStyle(
            cellValue,
            columnConfig.displayType,
            columnConfig.columnCondition,
            columnConfig.isBlank,
            columnConfig.decimalPlaces);
    }

    private getExportColumns(columns: Column[]): ExportColumn[] {
        return columns.map((column) => {
            const columnDefinition = column.getColDef();
            const headerName = columnDefinition.headerName;
            return {
                columnId: columnDefinition.field ?? columnDefinition.colId ?? '',
                columnName: columnDefinition.field === CrosstalkFields.Attachments ? 'Attachments' : headerName ?? '',
            };
        });
    }

    private getExportColumnsForGroupedGrid(columns: ExportColumn[], rowGroupColumns: Column[], onlySelectedRows: boolean): ExportColumn[] {
        columns.splice(columns.findIndex((column) => column.columnId === AUTO_GROUP_COLUMN_ID), 1);
        const columnNames = columns.map((column) => column.columnName);
        const groupedColumns = rowGroupColumns.map((column) => {
            const columnDefinition = column.getColDef();
            return {
                columnId: columnDefinition.field ?? columnDefinition.colId ?? '',
                columnName: columnDefinition.field === CrosstalkFields.Attachments ?
                    (!onlySelectedRows && columnNames.includes('Attachments') ? 'Attachments___Grouper___' : 'Attachments') :
                    (!onlySelectedRows && columnNames.includes(columnDefinition.headerName ?? '') ?
                        `${columnDefinition.headerName}___Grouper___` :
                        `${columnDefinition.headerName}`
                    ),
            };
        });
        return [...groupedColumns, ...columns];
    }

    private getExportData(
        columns: ExportColumn[],
        rows: any[], // eslint-disable-line @typescript-eslint/no-explicit-any
        allColumns: Column[],
        groupColumns?: ExportColumn[],
        datasetIncludesCrosstalk?: boolean,
    ): object[] {
        if (rows.length) {
            return rows.map((currRowData) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const row: any = {};
                columns.forEach((value) => {
                    const columnName = value.columnName;
                    row[columnName] = currRowData[value.columnId] ?? '';
                });

                if (groupColumns) {
                    row._row_id = currRowData._row_id;
                    row._parent_id = currRowData._parent_id;
                    row._group = currRowData.group;
                }

                if (datasetIncludesCrosstalk) {
                    row.conversationId = currRowData.conversationId;
                }

                return row;
            });
        } else {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const row: any = {};
            columns.forEach((value) => {
                const columnName = value.columnName;
                row[columnName] = '';
            });
            return [row];
        }
    }

    private getFilterFromState(stateFilter?: AgGridFilterState): AGFilter | null {
        if (isAgGridSetFilterState(stateFilter)) {
            return { includedStrings: JSON.stringify(stateFilter.values) };
        } else if (isAgGridDateMultiConditionFilterState(stateFilter)) {
            return {
                condition1: JSON.stringify(stateFilter.conditions[0]),
                condition2: JSON.stringify(stateFilter.conditions[1]),
                filterType: stateFilter.filterType,
                operator: stateFilter.operator,
            };
        } else if (isAgGridDateFilterState(stateFilter)) {
            return {
                filterFrom: stateFilter.dateFrom,
                filterTo: stateFilter.dateTo,
                filterType: stateFilter.filterType,
                type: stateFilter.type,
            };
        } else if (isAgGridNumberMultiConditionFilterState(stateFilter)) {
            return {
                condition1: JSON.stringify(stateFilter.conditions[0]),
                condition2: JSON.stringify(stateFilter.conditions[1]),
                filterType: stateFilter.filterType,
                operator: stateFilter.operator,
            };
        } else if (isAgGridNumberFilterState(stateFilter)) {
            return {
                filterFrom: stateFilter.filter != null ? String(stateFilter.filter) : '',
                filterTo: stateFilter.filterTo != null ? String(stateFilter.filterTo) : '',
                filterType: stateFilter.filterType,
                type: stateFilter.type,
            };
        }

        return null;
    }

    private getCellStyle(
        value: string,
        displayType?: ColumnDisplayType,
        formatting?: CellFormat[],
        isBlank?: boolean,
        decimalPlaces?: number,
    ): CellStyle {
        try {
            const style = getCellStyle(value, displayType, formatting, isBlank, decimalPlaces);
            const colorVar = this.colorMetadataService.mapColorToVariable(
                String(style.color ?? ''),
                String(style.backgroundColor ?? ''),
            );
            style.color = colorVar ? `var(--${colorVar}-color)` : '';
            style.backgroundColor = colorVar ? `var(--${colorVar}-background)` : '';

            return style;
        } catch (e) {
            this.errorLoggerService.logError('', e as Error);
            return {};
        }
    }

    private getFormattedValue(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        params: ValueFormatterParams | any,
        columnConfig: GridConfigItem,
        vizConfigs: VisualizationConfigs,
        namedQueryId: string,
        metadata: FieldMetadata | undefined,
        widgetId?: number,
    ): string {
        let value: string | number = params.value;

        if (typeof params.value === 'object' &&
            this.isUserDefinedFieldColumn(columnConfig.value) &&
            (params.value && params.value.type !== 'boolean')
        ) {
            if (params.value.type === 'decimal') {
                value = Number(params.value.value);
            } else {
                value = params.value.value;
            }
        }

        const rowNode: IRowNode = params.node;
        if (typeof params.value === 'undefined' ||
            (rowNode.group && params.column.colId !== AUTO_GROUP_COLUMN_ID && !vizConfigs.showSubTotal)) {
            return '';
        }

        let formattedValue = this.getFormattedValueByDisplayType(columnConfig, value, namedQueryId, metadata);

        const link: LinkConfiguration = params.data && this.getExternalLinkConfiguration(columnConfig.value, params.data);
        if (this.shouldLinkifyColumn(columnConfig.value, !!vizConfigs.showHyperlinks, !!link, widgetId ?? MANAGE_WIDGET_ID)) {
            const cellValue = {
                formatted: formattedValue,
                original: params.value,
            };

            formattedValue = this.crosstalkModalService.configureCellForLinking(link, cellValue, false, metadata);
        }

        if (columnConfig.hyperlink) {
            formattedValue = this.getHyperlinkFormattedValue(columnConfig, params, vizConfigs, widgetId as number, formattedValue);
        }

        if (columnConfig.linkBehavior === 'masterdetail' && columnConfig.ddvWidget) {
            formattedValue = this.getMasterDetailFormattedValue(columnConfig, params, formattedValue);
        }

        return formattedValue ?? '';
    }

    private shouldLinkifyColumn(columnName: string, showHyperlinks: boolean, isLink: boolean, widgetId: number): boolean {
        const widgetPrefs = this.getWidgetPreferences(widgetId);
        const isSimpleGrid = widgetPrefs?.currentVisualization === 'SIMPLE_GRID';
        const isCommentColumn = columnName === TrebekConversationFields.HSComment || columnName === TrebekConversationFields.ClientComment;

        return showHyperlinks && isLink && !(isSimpleGrid && isCommentColumn);
    }

    private renderProgressBar(value: number, barFormat: CellFormat | undefined, minMax: ColumnMinMax): string {
        if (!barFormat || isNaN(value) || isNaN(minMax.min) || isNaN(minMax.max) ||
            (barFormat.operatorCondition !== 'AUTO' && (isNaN(Number(barFormat.rangeFrom)) || isNaN(Number(barFormat.rangeTo))))) {
            return '';
        }
        let width: string;
        let newValue = value;
        let newMinMax = minMax;
        if (barFormat.operatorCondition === 'NUM') {
            newMinMax = {
                min: Number(barFormat.rangeFrom),
                max: Number(barFormat.rangeTo),
            };
        } else if (barFormat.operatorCondition === 'PRCT') {
            newValue = this.getScaledPercentageValue(value, minMax, false) as number;
            newMinMax = {
                min: Number(barFormat.rangeFrom),
                max: Number(barFormat.rangeTo),
            };
        }
        if (newValue >= newMinMax.max) {
            width = '100%';
        } else if (newValue <= newMinMax.min) {
            width = '0%';
        } else {
            width = this.getScaledPercentageValue(newValue, newMinMax, true) as string;
        }
        return `<div class="div-outer-div">
        <div class="div-percent-bar" style="width: ${width}; background-color: ${barFormat.barColor}"></div></div>`;
    }

    private getScaledPercentageValue(value: number, minMax: ColumnMinMax, isDisplay: boolean): string | number {
        // refer FXB-1118 for the formula
        const numerator = value - minMax.min;
        const denominator = minMax.max - minMax.min;
        return (isDisplay ? percentageFormatter.asDisplayValue(numerator / denominator, 2) :
            percentageFormatter.asFilterValue(numerator / denominator, 2)) ?? '';
    }

    private getFormattedNumber(
        value: number,
        decimalPlaces: number,
        numberFormat: NumberFormat | '',
        negativeFormat: ColumnNegativeValues,
    ): string {
        const formatted = this.formatter.summaryFormatting(
            value,
            decimalPlaces,
            numberFormat,
            'actual',
            negativeFormat.includes('arenthesis'),  // matches both "parenthesis" and "redParenthesis"

        );
        return formatted.summaryColumnValue;
    }

    private getColumnFilterType(displayType?: ColumnDisplayType): string {
        switch (displayType) {
            case 'string':
            case 'boolean':
                return 'customColumnFilterComponent';
            case 'date':
                return 'agDateColumnFilter';
            default:
                return 'agNumberColumnFilter';
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private getColumnFilterParams(columnConfig: GridConfigItem, widgetId: number): any {
        const queryTypeName = this.getCurrentNamedQuery(widgetId)?.normalizeQueryTypeName();
        const filterParams: CustomFilterParams = {
            isTFLDetailsNamedQuery: isTradeFileDetails(queryTypeName),
            inRangeInclusive: true,
            debounceMs: 500,
        };

        // We are limiting the filter options when the column is ranked due to a bug
        // After data rows are filtered, ranked columns are recomputed and they start again from 1,
        // no matter that their actual value is different in the whole dataset
        if (columnConfig.isRanked) {
            filterParams.filterOptions = NUMBER_FILTER_OPERATIONS_FOR_RANKED_COLUMN;
        } else if (columnConfig.displayType === 'value' || columnConfig.displayType === 'bar') {
            filterParams.filterOptions = NUMBER_FILTER_OPERATIONS;
        }

        if (columnConfig.displayType === 'string') {
            filterParams.textFormatter = (value: unknown): string => value ? value.toString().toLowerCase() : '';
            // These would prevent some edge cases but they don't work: https://www.ag-grid.com/archive/15.0.0/javascript-grid-filter-set/
            // filterParams.suppressRemoveEntries = true;
            // filterParams.newRowsAction = 'keep';
        }

        if (columnConfig.displayType === 'date') {
            filterParams.comparator = (filterLocalDateAtMidnight: Date, cellValue: string): number => {
                const cellDate = new Date(cellValue);

                if (cellDate < filterLocalDateAtMidnight) {
                    return -1;
                }

                if (cellDate > filterLocalDateAtMidnight) {
                    return 1;
                }

                return 0;
            };
        }

        return filterParams;
    }

    private getColumnFilterValueGetter(params: ValueGetterParams, columnConfig: GridConfigItem): string | number | Date | undefined {
        const data = params.data ? params.data[columnConfig.value] : params.node?.aggData[columnConfig.colId];
        const isUserDefinedField = this.isUserDefinedFieldColumn(columnConfig.value);

        let userDefinedFieldColumnValue: string | undefined;
        if (isUserDefinedField) {
            userDefinedFieldColumnValue = this.getUserDefinedFieldValue(data, columnConfig);
        }

        const value = this.getColumnFilterValue(params, columnConfig, isUserDefinedField, userDefinedFieldColumnValue, data);
        const decimalPlaces = (columnConfig.numberFormat === 'XX' ? 0 : columnConfig.decimalPlaces) ?? 0;
        switch (columnConfig.displayType) {
            case 'boolean':
                return booleanFormatter.asFilterValue(value);
            case 'date':
                return dateFormatter.asFilterValue(value as string);
            case 'value':
                return columnConfig.numberFormat === '%' ?
                    percentageFormatter.asFilterValue(value, decimalPlaces) :
                    numberFormatter.asFilterValue(value, decimalPlaces);
            default:
                return value;
        }
    }

    private getColumnFilterValue(
        params: ValueGetterParams,
        columnConfig: GridConfigItem,
        isUserDefinedField: boolean,
        userDefinedFieldColumnValue: string | undefined,
        data: string | number,
    ): string | number | undefined {
        if (columnConfig.useAbsVal) {
            return params.data ?
                params.data[this.columnTransformService.getAbsValFieldName(columnConfig)] :
                params.node?.aggData[columnConfig.colId];
        }

        if (isUserDefinedField) {
            return userDefinedFieldColumnValue;
        }

        return data;
    }

    private getDatasetMinMax(namedQueryId: string, columnId: string | undefined): ColumnMinMax {
        return this.datasetMinMax[namedQueryId] && columnId ? this.datasetMinMax[namedQueryId][columnId] : { min: 0, max: 0 };
    }

    private isZero(columnConfig: GridConfigItem, value: number): boolean {
        if (value >= 1 || value <= -1) {
            return false;
        }

        const cellContent = this
            .getFormattedNumber(value, columnConfig.decimalPlaces ?? 0, columnConfig.numberFormat ?? '', columnConfig.negativeValue ?? 'minus')
            // eslint-disable-next-line no-useless-escape
            .replace(/[\($\-]+/, '')
            .substring(0, (columnConfig.decimalPlaces ? Number(columnConfig.decimalPlaces) : 0) + 2);
        return !cellContent.match(/[1-9]/);
    }

    private addColumnConditionCellRenderer(
        columnDef: ColDef,
        columnConfig: GridConfigItem,
        vizConfigs: VisualizationConfigs,
        namedQueryId: string,
        metadata: FieldMetadata | undefined,
    ): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        columnDef.valueFormatter = (params: ValueFormatterParams): any => columnConfig.isRanked ?
            this.columnRankingService.getFormattedValue(params, columnConfig) :
            params.node ?
                this.getFormattedValue(params, columnConfig, vizConfigs, namedQueryId, metadata) :
                null;
        columnDef.cellRenderer = ColumnConditionCellRendererComponent;
        columnDef.cellRendererParams = {
            columnCondition: columnConfig.columnCondition,
            displayType: columnConfig.displayType,
            namedQueryId,
        };
    }

    private addCustomNumberSort(columnDef: ColDef): void {
        columnDef.comparator = (valueA, valueB): number => Number(valueA) - Number(valueB);
    }

    private addCustomStringSort(columnDef: ColDef): void {
        columnDef.comparator = (a: string, b: string): number => {
            const valueA: string = a == null ? '' : a.toString();
            const valueB: string = b == null ? '' : b.toString();
            return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
        };
    }

    private addURLCellRenderer(
        columnDef: ColDef,
        columnConfig: GridConfigItem,
        vizConfigs: VisualizationConfigs,
        namedQueryId: string,
        metadata: FieldMetadata | undefined,
    ): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        columnDef.valueFormatter = (params: ValueFormatterParams): any => columnConfig.isRanked ?
            this.columnRankingService.getFormattedValue(params, columnConfig) :
            params.node ?
                this.getFormattedValue(params, columnConfig, vizConfigs, namedQueryId, metadata) :
                null;
        columnDef.cellRenderer = URLCellRendererComponent;
        columnDef.cellRendererParams = {
            ...columnDef.cellRendererParams,
            isRanked: columnConfig.isRanked,
        };
    }

    private onCellValueChanged(params: NewValueParams, widgetId: number): void {
        const colId = params.column.getColId();
        const colDef = params.column.getColDef() as CustomColDef;
        const field = colDef.field ?? '';

        if (
            isHSCommentOrClientCommentField(field) ||
            isAttachmentsOrCommentCounterField(field) ||
            isPicklistColumn(colDef) ||
            colDef.isUserDefinedField
        ) {
            return;
        }

        if (params.oldValue === params.newValue) {
            return;
        }

        const rowIndex = params.node?.id ?? '';
        if (!this.editedCellsOriginalValue[widgetId]) {
            this.editedCellsOriginalValue[widgetId] = {};
        }

        if (!this.editedCellsOriginalValue[widgetId][rowIndex]) {
            this.editedCellsOriginalValue[widgetId][rowIndex] = {};
        }

        if (!this.editedCellsOriginalValue[widgetId][rowIndex][colId]) {
            this.editedCellsOriginalValue[widgetId][rowIndex][colId] = {
                originalValue: params.oldValue,
            };
        }

        const originalValue = this.editedCellsOriginalValue[widgetId][rowIndex][colId].originalValue;
        this.editedCellsOriginalValue[widgetId][rowIndex][colId].hasChanged =
            !(originalValue == null && params.newValue === '') && originalValue !== params.newValue;

        this.toggleCheckboxOnCellValueChange(widgetId, rowIndex, params);
    }

    private toggleCheckboxOnCellValueChange(widgetId: number, rowIndex: string, params: NewValueParams): void {
        const queryTypeName = this.getCurrentNamedQuery(widgetId)?.normalizeQueryTypeName();
        const { data, parent } = params.node!;
        let isAllocation = false;
        let isBlock = false;
        if (isTradeFileDetails(queryTypeName)) {
            const isGridGroupedByAllocationGroupId = this.isGridGroupedByAllocationGroupId(params);
            isAllocation = this.isAllocation(isGridGroupedByAllocationGroupId, data);
            isBlock = this.isBlock(isGridGroupedByAllocationGroupId, data);
        }

        let block: IRowNode | undefined;
        if (isAllocation) {
            block = parent?.childrenAfterFilter?.find((child: IRowNode) => child.data?.[TRANSACTION_TYPE_FIELD]);
        }

        let blockContainsEditedCells = false;
        if (isBlock) {
            const ids = parent?.childrenAfterFilter?.map((child: IRowNode) => child.id);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const editedRows: any[] = [];
            ids?.forEach((id?: string) => {
                if (id && this.editedCellsOriginalValue[widgetId][id]) {
                    editedRows.push(this.editedCellsOriginalValue[widgetId][id]);
                }
            });

            editedRows.forEach((row) => {
                if (!blockContainsEditedCells) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    blockContainsEditedCells = Object.values(row).some((col: any) => col.hasChanged);
                }
            });
        }

        const rowToCheck = !isAllocation ? params.node : block;
        const currentRow = this.editedCellsOriginalValue[widgetId][rowIndex];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const rowContainsEditedCells = Object.values(currentRow).some((col: any) => col.hasChanged);
        const checkboxValue = params.colDef.field === moTradeReleaseCommentFieldId ?
            false :
            rowContainsEditedCells || blockContainsEditedCells;
        const updatedData = rowToCheck?.data;
        updatedData[EDITABLE_CHECKBOX_FIELD] = checkboxValue;
        rowToCheck?.setData(updatedData);
        rowToCheck?.setSelected(checkboxValue);

        const rowsToRedraw: IRowNode[] = [];
        if (!isAllocation && params.node) {
            rowsToRedraw.push(params.node);
        } else if (params.node && block) {
            rowsToRedraw.push(block);
            rowsToRedraw.push(params.node);
        }
        params.api.redrawRows({ rowNodes: rowsToRedraw });
        params.api.refreshHeader();
    }

    private getCSVFilteredExportGroupRow(rowDatum: IRowNode, showGrouperCount: boolean): object {
        const datum = deepClone(deepExtend({}, [rowDatum.groupData, rowDatum.aggData]));
        for (const key in datum) {
            if (typeof datum[key] !== 'undefined') {
                if (key === AUTO_GROUP_COLUMN_ID && showGrouperCount) {
                    datum[key] = datum[key] ? `${datum[key]}(${rowDatum.allChildrenCount})` : `(${rowDatum.allChildrenCount})`;
                } else if (typeof datum[key] === 'object' && datum[key] != null) {
                    datum[key] = datum[key].toNumber();
                }
            }
        }
        return datum;
    }

    private getCSVFilteredExportGroupRowInPivotMode(rowDatum: IRowNode, showGrouperCount: boolean, columns: Column[]): object {
        const groupData = getValue(rowDatum.groupData?.[AUTO_GROUP_COLUMN_ID] as string | null | undefined);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const rowData: any = {};

        let newRowDatum = rowDatum;
        while (newRowDatum.group && newRowDatum.childrenAfterSort?.length) {
            const children = newRowDatum.childrenAfterSort;
            if (children[0].group) {
                rowData[children[0].field ?? ''] = getValue(children[0].groupData?.[AUTO_GROUP_COLUMN_ID] as string | null | undefined);
            }
            newRowDatum = children[0];
        }

        newRowDatum = rowDatum;
        while (newRowDatum.parent?.groupData && !rowData[newRowDatum.parent.field ?? '']) {
            const parent = newRowDatum.parent;
            rowData[parent.field ?? ''] = getValue(parent.groupData?.[AUTO_GROUP_COLUMN_ID] as string | null | undefined);
            newRowDatum = parent;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const aggData: any = {};
        for (const key in rowDatum.aggData) {
            if (typeof rowDatum.aggData[key] !== 'undefined') {
                const keyToUse = key.includes(RANK_SUFFIX) ? key.slice(0, key.indexOf(RANK_SUFFIX)) : key;
                const newKey = this.getColDefField(keyToUse, columns);
                aggData[newKey ?? ''] = rowDatum.aggData[key];
            }
        }

        return {
            [rowDatum.field ?? '']: showGrouperCount ? `${groupData}(${rowDatum.allChildrenCount})` : groupData,
            ...rowData,
            ...aggData,
        };
    }

    private getExcelFilteredExportGroupRow(
        rowDatum: IRowNode,
        showGrouperCount: boolean,
        columns: Column[],
        showSubtotal: boolean | null = false,
    ): object {
        const groupData = rowDatum.groupData?.[AUTO_GROUP_COLUMN_ID];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const aggData: any = {};
        for (const key in rowDatum.aggData) {
            if (typeof rowDatum.aggData[key] !== 'undefined' && showSubtotal) {
                const newKey = this.getColDefField(key, columns);
                aggData[newKey ?? ''] = rowDatum.aggData[key];
            }
        }
        return {
            [rowDatum.field ?? '']: showGrouperCount ? `${groupData}(${rowDatum.allChildrenCount})` : groupData,
            ...aggData,
            ...rowDatum.data,
            childrenIds: rowDatum.childrenAfterSort?.map((child) => child.id),
        };
    }

    private getExcelFilteredExportGroupRowInPivotMode(
        rowDatum: IRowNode,
        showGrouperCount: boolean,
        columns: Column[],
        showSubtotal: boolean | null = false,
    ): object {
        const groupData = getValue(rowDatum.groupData?.[AUTO_GROUP_COLUMN_ID] as string | null | undefined);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const rowData: any = {};
        for (const key in rowDatum.aggData) {
            if (typeof rowDatum.aggData[key] !== 'undefined' && showSubtotal) {
                const keyToUse = key.includes(RANK_SUFFIX) ? key.slice(0, key.indexOf(RANK_SUFFIX)) : key;
                const newKey = this.getColDefField(keyToUse, columns);
                rowData[newKey ?? ''] = rowDatum.aggData[key];
            }
        }
        return {
            [rowDatum.field ?? '']: showGrouperCount ? `${groupData}(${rowDatum.allChildrenCount})` : groupData,
            ...rowData,
            childrenIds: rowDatum.childrenAfterSort?.map((child) => child.id),
        };
    }

    // used both for CSV and Excel for now
    private getFilteredExportRowInPivotMode(rowDatum: IRowNode | undefined, columns: Column[]): object {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const aggData: any = {};
        for (const key in rowDatum?.aggData) {
            if (typeof rowDatum?.aggData[key] !== 'undefined') {
                const newKey = this.getColDefField(key, columns);
                aggData[newKey ?? ''] = rowDatum.aggData[key];
            }
        }
        return aggData;
    }

    private getColDefField(colId: string, columns: Column[]): string | undefined {
        return columns.find((column) => column.getColId() === colId)?.getColDef().field;
    }

    private columnShouldBeLinkified(
        columnConfig: GridConfigItem,
        columnDef: ColDef | CustomColDef,
        metadata: FieldMetadata | undefined,
    ): boolean {
        const { hyperlink } = columnConfig;
        const { field } = columnDef as CustomColDef;
        const editable = !!metadata?.editable;
        const isDatatypeDecimalOrPercentage = columnConfig.datatype === 'decimal' || columnConfig.datatype === 'percentage';
        const isCrosstalkColumn =
            isHSCommentOrClientCommentField(field) ||
            field === `${TrebekConversationFields.HSComment}Author` ||
            field === `${TrebekConversationFields.ClientComment}Author` ||
            field === `${TrebekConversationFields.HSComment}Created` ||
            field === `${TrebekConversationFields.ClientComment}Created` ||
            isAttachmentsOrCommentCounterField(field) ||
            field === crosstalkCheckboxFieldId ||
            this.isUserDefinedFieldColumn(field);
        return !isDatatypeDecimalOrPercentage && !editable && !hyperlink && !isCrosstalkColumn && field !== EDITABLE_CHECKBOX_FIELD;
    }

    private getFormattedValueByDisplayType(
        columnConfig: GridConfigItem,
        value: string | number,
        namedQueryId: string,
        metadata: FieldMetadata | undefined,
    ): string {
        let formattedValue = '';
        switch (columnConfig.displayType) {
            case 'boolean':
                formattedValue = booleanFormatter.asDisplayValue(value);
                break;
            case 'string':
                formattedValue = columnConfig.datatype === 'date' ?
                    dateFormatter.asDisplayValue(value, { input: 'YYYY-MM-DD', output: metadata?.format ?? '' }) :
                    value as string;
                break;
            case 'date':
                formattedValue = dateFormatter.asDisplayValue(value ?? '', {
                    input: 'YYYY-MM-DD',
                    output: columnConfig.dateFormat ?? 'MM/DD/YYYY',
                });
                break;
            case 'value':
                formattedValue = this.getFormattedValueForDisplayTypeValue(columnConfig, value);
                break;
            case 'bar':
                formattedValue = this.renderProgressBar(
                    value as number,
                    columnConfig.columnCondition?.[0],
                    this.getDatasetMinMax(namedQueryId, columnConfig.value));
        }
        return formattedValue;
    }

    private getFormattedValueForDisplayTypeValue(
        columnConfig: GridConfigItem,
        valueObject: string | number | Partial<{ count: number, value: number }>,
    ): string {
        if (typeof valueObject === 'number' || typeof valueObject === 'object') {
            const value = typeof valueObject === 'number' ?
                valueObject :
                valueObject && 'value' in valueObject ? (valueObject.value ?? 0) : 0;

            if (columnConfig.isBlank && (value === 0 || this.isZero(columnConfig, value))) {
                return '';
            }

            const formattedValue = this.getFormattedNumber(
                value,
                columnConfig.decimalPlaces ?? 0,
                columnConfig.numberFormat ?? '',
                columnConfig.negativeValue ?? 'minus');

            return formattedValue;
        }

        return valueObject;
    }

    private getHyperlinkFormattedValue(
        columnConfig: GridConfigItem,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        params: any,
        vizConfigs: VisualizationConfigs,
        widgetId: number,
        formattedValue: string,
    ): string {
        let uri = `${columnConfig.hyperlink}`;
        if (this.isMultiClient) {
            const queryPeriodTypeName = this.getCurrentWidgetNamedQuery(widgetId)?.periodType;
            uri = this.gridBuilderService.buildUriWithDynamicValues(uri, params, vizConfigs, queryPeriodTypeName);
        }

        const cellValue = {
            formatted: formattedValue,
            original: params.value,
        };
        const hyperlink: LinkConfiguration = {
            label: params.value,
            uri,
            launchType: columnConfig.linkBehavior ?? '_self',
        };

        return this.crosstalkModalService.configureCellForLinking(hyperlink, cellValue, true);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private getMasterDetailFormattedValue(columnConfig: GridConfigItem, params: any, formattedValue: string): string {
        const cellValue = {
            formatted: formattedValue,
            original: params.value,
        };
        const hyperlink: LinkConfiguration = {
            label: params.value,
            uri: columnConfig.ddvWidget?.toString() ?? '',
            launchType: columnConfig.linkBehavior,
        };
        return this.crosstalkModalService.configureCellForLinking(hyperlink, cellValue, true);
    }

    private getExportRowData(
        rowDatum: IRowNode,
        inPivotMode: boolean,
        autoColumnGroup: boolean,
        index: number,
        isCSV: boolean,
        preferences: VisualizationConfigs | undefined,
        allColumns: Column[],
        dataGridComponent: DataGridComponent,
    ): any { // eslint-disable-line @typescript-eslint/no-explicit-any
        let data: any; // eslint-disable-line @typescript-eslint/no-explicit-any
        if (inPivotMode) {
            if (autoColumnGroup) {
                if (rowDatum.group) {
                    if (isCSV) {
                        if (rowDatum.leafGroup) {
                            data = this.getCSVFilteredExportGroupRowInPivotMode(rowDatum, !!preferences?.showGrouperCount, allColumns);
                        }
                    } else {
                        data = this.getExcelFilteredExportGroupRowInPivotMode(
                            rowDatum,
                            !!preferences?.showGrouperCount,
                            allColumns,
                            preferences?.showSubTotal,
                        );
                    }
                }
            } else if (index === 1) {
                const visibleRow = dataGridComponent.grid.api?.getRenderedNodes()[0];
                data = this.getFilteredExportRowInPivotMode(visibleRow, allColumns);
            }
        } else {
            if (rowDatum.group) {
                data = isCSV ?
                    this.getCSVFilteredExportGroupRow(rowDatum, !!preferences?.showGrouperCount) :
                    this.getExcelFilteredExportGroupRow(rowDatum, !!preferences?.showGrouperCount, allColumns, preferences?.showSubTotal);
            } else {
                data = rowDatum.data;
            }
        }
        return data;
    }

    private getUserDefinedFieldValue(
        data: any, // eslint-disable-line @typescript-eslint/no-explicit-any
        columnConfig: GridConfigItem,
    ): string | undefined {
        const userDefinedFieldType = columnConfig.datatype;
        let userDefinedFieldColumnValue: string | undefined;
        switch (userDefinedFieldType) {
            case 'choice':
            case 'boolean':
            case 'string':
            case 'number':
            case 'decimal':
                userDefinedFieldColumnValue = data?.value;
                break;
            case 'date':
                userDefinedFieldColumnValue = data ? data.value : data;
        }
        return userDefinedFieldColumnValue;
    }

    private isGridGroupedByAllocationGroupId(params: NewValueParams): boolean {
        const groupColumns = params.api.getRowGroupColumns();
        return groupColumns.length === 1 && groupColumns[0].getColDef().field === TFL_DETAILS_AUTO_GROUP_COLUMN_ID;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private isAllocation(isGridGrouped: boolean, data: any): boolean {
        return !!(isGridGrouped && data?.[TFL_DETAILS_AUTO_GROUP_COLUMN_ID] && !data[TRANSACTION_TYPE_FIELD]);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private isBlock(isGridGrouped: boolean, data: any): boolean {
        return !!(isGridGrouped && data?.[TFL_DETAILS_AUTO_GROUP_COLUMN_ID] && data[TRANSACTION_TYPE_FIELD]);
    }

    private shouldBeExported(onlySelectedRows: boolean, node: IRowNode): boolean {
        return !onlySelectedRows ||
            (onlySelectedRows && node.isSelected()) ||
            node.data?.[EDITABLE_CHECKBOX_FIELD];
    }

    private addGrandTotalRow(
        dataGridComponent: DataGridComponent,
        preferences: VisualizationConfigs | undefined,
        isCSV: boolean,
        allColumns: Column[],
        rowData: any[], // eslint-disable-line @typescript-eslint/no-explicit-any
    ): void {
        const { columnState } = dataGridComponent.getState();
        const autoColumnGroup = columnState.find((col) => col.colId === AUTO_GROUP_COLUMN_ID);

        if (autoColumnGroup && !isCSV && preferences?.showSubTotal) {
            const grandTotalData = dataGridComponent.getGrandTotalData();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const grandTotalRow: any = { _row_id: 0 };
            for (const key in grandTotalData) {
                if (typeof key !== 'undefined') {
                    const newKey = this.getColDefField(key, allColumns);
                    grandTotalRow[newKey ?? ''] = grandTotalData[key];
                }
            }
            rowData.push(grandTotalRow);
        }
    }

    private assignUDFValue(
        udfType?: UserDefinedFieldType,
        value?: string | number | boolean | null,
    ): string | number | boolean | CommonDate | undefined {
        switch (udfType) {
            case 'choice':
                return String(value ?? '');
            case 'date':
                return value ? CommonDate.fromDashFormat(value as string) : undefined;
            case 'decimal':
                return Number(value ?? 0);
            case 'string':
                return String(value ?? '');
        }
    }
}

export function isManageWidgetMode(widgetId: number): boolean {
    return widgetId === MANAGE_WIDGET_ID;
}

function getValue(value: string | null | undefined): string {
    if (value == null || value === '') {
        return 'Undefined';
    }

    return value;
}

function getParsedValue(params: ValueParserParams): string | number {
    if (typeof params.oldValue === 'number') {
        return Number(params.newValue);
    }
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    return params.newValue !== undefined ? params.newValue : '';
}

function isHSCommentOrClientCommentField(field?: string): boolean {
    return field === TrebekConversationFields.HSComment || field === TrebekConversationFields.ClientComment;
}

function isAttachmentsOrCommentCounterField(field?: string): boolean {
    return field === CrosstalkFields.Attachments || field === CrosstalkFields.CommentCounter;
}

function isPicklistColumn(colDef: CustomColDef): boolean {
    return !!colDef.cellRendererParams?.picklist;
}
